/**
 * @fileoverview API client for the Invoices feature.
 * @module features/invoices/api/invoicesService
 */

import apiClient from '@/lib/apiClient'
import {
  invoiceSchema,
  invoicePageSchema,
  createInvoiceRequestSchema,
  updateInvoiceRequestSchema,
  parsedInvoiceResponseSchema,
  invoiceLinksResponseSchema,
  linkInvoicePaymentsRequestSchema,
  type Invoice,
  type InvoicePage,
  type CreateInvoiceRequest,
  type UpdateInvoiceRequest,
  type ParsedInvoiceResponse,
  type LinkInvoicePaymentsRequest,
  type InvoiceLinksResponse,
} from '../schemas/invoicesSchema'

const API_BASE = '/invoices'

// ── List / Get ────────────────────────────────────────────────────────

export interface ListInvoicesParams {
  page?: number
  size?: number
  startDate?: string
  endDate?: string
}

export async function listInvoices(params: ListInvoicesParams = {}): Promise<InvoicePage> {
  const { page = 0, size = 20, startDate, endDate } = params
  const res = await apiClient.get(API_BASE, {
    params: {
      page,
      size,
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    },
  })
  const parsed = invoicePageSchema.safeParse(res.data)
  if (!parsed.success) throw new Error('API validation failed for GET /invoices')
  return parsed.data
}

export async function getInvoice(uuid: string): Promise<Invoice> {
  const res = await apiClient.get(`${API_BASE}/${encodeURIComponent(uuid)}`)
  const parsed = invoiceSchema.safeParse(res.data)
  if (!parsed.success) throw new Error('API validation failed for GET /invoices/:uuid')
  return parsed.data
}

// ── Create / Update / Delete ──────────────────────────────────────────

export async function createInvoice(body: CreateInvoiceRequest): Promise<Invoice> {
  const validated = createInvoiceRequestSchema.parse(body)
  const res = await apiClient.post(API_BASE, validated)
  const parsed = invoiceSchema.safeParse(res.data)
  if (!parsed.success) throw new Error('API validation failed for POST /invoices')
  return parsed.data
}

export async function updateInvoice(uuid: string, body: UpdateInvoiceRequest): Promise<Invoice> {
  const validated = updateInvoiceRequestSchema.parse(body)
  const res = await apiClient.put(`${API_BASE}/${encodeURIComponent(uuid)}`, validated)
  const parsed = invoiceSchema.safeParse(res.data)
  if (!parsed.success) throw new Error('API validation failed for PUT /invoices/:uuid')
  return parsed.data
}

export async function deleteInvoice(uuid: string): Promise<void> {
  await apiClient.delete(`${API_BASE}/${encodeURIComponent(uuid)}`)
}

// ── Parse ─────────────────────────────────────────────────────────────

export async function parseInvoiceFile(file: File): Promise<ParsedInvoiceResponse> {
  const form = new FormData()
  form.append('file', file)
  const res = await apiClient.post(`${API_BASE}/parse`, form)
  const parsed = parsedInvoiceResponseSchema.safeParse(res.data)
  if (!parsed.success) throw new Error('API validation failed for POST /invoices/parse')
  return parsed.data
}

// ── File operations ───────────────────────────────────────────────────

export async function uploadInvoiceFile(uuid: string, file: File): Promise<Invoice> {
  const form = new FormData()
  form.append('file', file)
  const res = await apiClient.post(`${API_BASE}/${encodeURIComponent(uuid)}/file`, form)
  const parsed = invoiceSchema.safeParse(res.data)
  if (!parsed.success) throw new Error('API validation failed for POST /invoices/:uuid/file')
  return parsed.data
}

export function getInvoiceFileUrl(uuid: string): string {
  return `${API_BASE}/${encodeURIComponent(uuid)}/file`
}

export async function downloadInvoiceFile(uuid: string): Promise<Blob> {
  const res = await apiClient.get(`${API_BASE}/${encodeURIComponent(uuid)}/file`, {
    responseType: 'blob',
  })
  return res.data as Blob
}

// ── Payment links ──────────────────────────────────────────────────────

export async function linkPaymentsToInvoice(
  uuid: string,
  body: LinkInvoicePaymentsRequest,
): Promise<InvoiceLinksResponse> {
  const validated = linkInvoicePaymentsRequestSchema.parse(body)
  const res = await apiClient.post(`${API_BASE}/${encodeURIComponent(uuid)}/links`, validated)
  const parsed = invoiceLinksResponseSchema.safeParse(res.data)
  if (!parsed.success) throw new Error('API validation failed for POST /invoices/:uuid/links')
  return parsed.data
}

export async function getInvoiceLinks(uuid: string): Promise<InvoiceLinksResponse> {
  const res = await apiClient.get(`${API_BASE}/${encodeURIComponent(uuid)}/links`)
  const parsed = invoiceLinksResponseSchema.safeParse(res.data)
  if (!parsed.success) throw new Error('API validation failed for GET /invoices/:uuid/links')
  return parsed.data
}

export async function unlinkPaymentFromInvoice(uuid: string, paymentUuid: string): Promise<void> {
  await apiClient.delete(
    `${API_BASE}/${encodeURIComponent(uuid)}/links/${encodeURIComponent(paymentUuid)}`,
  )
}

export default {
  listInvoices,
  getInvoice,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  parseInvoiceFile,
  uploadInvoiceFile,
  downloadInvoiceFile,
  getInvoiceLinks,
  linkPaymentsToInvoice,
  unlinkPaymentFromInvoice,
}
